import { EventCriticality, normalizeEvent, criticalityFromName, criticalityLabel, describeEvent, resourceLabel, EVENT_CRITICALITY_ORDER, EVENT_RESOURCE_ORDER } from '@authe/fe-commons/events';
import { storageKey, readLocalJson, writeLocalJson } from '@authe/fe-commons/storage';
import { STREAM_NOTIFICATIONS, streamChannelIdsForCaller } from '@authe/streaming';
import { unwrapData, unwrapResponse } from '@authe/fe-commons/api';
import * as React from 'react';
import { createLogger } from '@authe/fe-commons/logging';
import { notify, CriticalityBadge, cn, Button, ErrorState, Skeleton, EmptyState, ScrollArea, Separator, Popover, PopoverTrigger, Badge, PopoverContent, Label, Switch, Card, CardHeader, CardTitle, Tabs, TabsList, TabsTrigger, EnumSelect, Input, CardContent } from '@authe/ui-core';
import { formatRelativeTime, formatTimestamp } from '@authe/fe-commons/time';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

// src/config.ts
var NC_PACKAGE = "notification-center";
var NC_READ_STATE_KEY = storageKey(NC_PACKAGE, "read");
var NC_DEFAULT_PAGE_SIZE = 20;
var NC_DEFAULT_TOAST_THRESHOLD = EventCriticality.HIGH;
var NC_POPOVER_MAX_HEIGHT = 384;
var NC_DEFAULT_MAX_BADGE = 99;
var NC_ALL_TAB = "all";
var NC_FILTER_DEBOUNCE_MS = 250;
var NC_STREAM = STREAM_NOTIFICATIONS;
function notificationChannelsFor(scope, teamIds = []) {
  return streamChannelIdsForCaller(scope, NC_STREAM, teamIds);
}
function isTenancyFiltered(filter) {
  return Boolean(
    filter.customerIds?.length || filter.actorUserIds?.length || filter.orgId || filter.tenantId || filter.audienceAny
  );
}
function serverFilterKey(filter) {
  return JSON.stringify([
    filter.criticalities ?? null,
    filter.resources ?? null,
    filter.unreadOnly ?? null,
    filter.includeArchived ?? null,
    filter.search?.trim() || null,
    filter.customerIds ?? null,
    filter.orgId ?? null,
    filter.tenantId ?? null,
    filter.actorUserIds ?? null,
    filter.audienceAny ?? null
  ]);
}
function eventTimestampMs(event) {
  const raw = event.created_at;
  if (typeof raw === "number") return raw < 1e12 ? raw * 1e3 : raw;
  if (typeof raw === "string") {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1e3 : numeric;
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
}
function toNotification(event, overrides = {}) {
  const normalized = normalizeEvent(event);
  return {
    event: normalized,
    read: false,
    archived: false,
    receivedAt: eventTimestampMs(normalized),
    ...overrides
  };
}
function matchesFilter(n, filter) {
  if (n.archived && !filter.includeArchived) return false;
  if (filter.unreadOnly && n.read) return false;
  const { criticalities, resources, search } = filter;
  if (criticalities && criticalities.length > 0 && !criticalities.includes(n.event.criticality_level)) {
    return false;
  }
  if (resources && resources.length > 0 && !resources.includes(n.event.resource)) {
    return false;
  }
  if (search && search.trim()) {
    const q = search.trim().toLowerCase();
    const haystack = `${n.event.message_summary ?? ""} ${n.event.message ?? ""}`.toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}
function byRecency(a, b) {
  return b.receivedAt - a.receivedAt;
}

// src/data/source.ts
function createInMemoryNotificationSource(seed = [], options = {}) {
  const pageSize = options.pageSize ?? 20;
  const store = [...seed];
  const listeners = /* @__PURE__ */ new Set();
  return {
    async fetchHistory({ cursor, limit }) {
      const start = cursor ? Number(cursor) || 0 : 0;
      const size = limit ?? pageSize;
      const items = store.slice(start, start + size);
      const next = start + size;
      return { items, nextCursor: next < store.length ? String(next) : null };
    },
    async markRead() {
    },
    async markAllRead() {
    },
    async archive(ids) {
      for (const id of ids) {
        const idx = store.findIndex((e) => e.id === id);
        if (idx >= 0) store.splice(idx, 1);
      }
    },
    subscribe(onEvent) {
      listeners.add(onEvent);
      return () => {
        listeners.delete(onEvent);
      };
    },
    emit(event) {
      store.unshift(event);
      for (const listener of listeners) listener(event);
    }
  };
}
function createApiNotificationSource(client, options = {}) {
  const base = options.basePath ?? "/notifications";
  return {
    async fetchHistory(query) {
      const { filter } = query;
      const res = unwrapResponse(
        await client.get(base, {
          query: {
            cursor: query.cursor,
            limit: query.limit,
            criticalities: filter?.criticalities?.map(String),
            resources: filter?.resources?.map(String),
            unreadOnly: filter?.unreadOnly,
            includeArchived: filter?.includeArchived,
            search: filter?.search,
            // Tenancy ladder. Param names match the handler's query contract;
            // the server authorizes every one of them, so sending them from a
            // caller who may not widen produces a 403, never a wider read.
            customerIds: filter?.customerIds?.map(String),
            orgId: filter?.orgId,
            tenantId: filter?.tenantId,
            actorUserIds: filter?.actorUserIds?.map(String),
            audienceAny: filter?.audienceAny
          }
        })
      );
      return { items: res.data ?? [], nextCursor: res.pagination?.next ?? null };
    },
    async markRead(ids) {
      unwrapData(await client.post(`${base}/read`, { ids }));
    },
    async markAllRead() {
      unwrapData(await client.post(`${base}/read-all`));
    },
    async archive(ids) {
      unwrapData(await client.post(`${base}/archive`, { ids }));
    },
    ...options.subscribe ? { subscribe: options.subscribe } : {}
  };
}

// src/data/mergeSources.ts
function mergeNotificationSources(base, live) {
  if (!live) return base;
  const baseSubscribe = base.subscribe?.bind(base);
  return {
    ...base,
    subscribe(onEvent) {
      const unsubLive = live.subscribe(onEvent);
      const unsubBase = baseSubscribe?.(onEvent);
      return () => {
        unsubLive();
        unsubBase?.();
      };
    }
  };
}
var log = createLogger(NC_PACKAGE);
function toastTypeFor(level) {
  if (level <= EventCriticality.CRITICAL) return "error";
  if (level === EventCriticality.HIGH) return "warning";
  if (level === EventCriticality.SUCCESS) return "success";
  return "info";
}
function useNotifications(options) {
  const {
    source,
    initialFilter = {},
    toastAtOrAbove = NC_DEFAULT_TOAST_THRESHOLD,
    persistKey = NC_READ_STATE_KEY,
    pageSize = NC_DEFAULT_PAGE_SIZE,
    autoLoad = true
  } = options;
  const [items, setItems] = React.useState([]);
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState(initialFilter);
  const [cursor, setCursor] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(false);
  const readIdsRef = React.useRef(
    new Set(persistKey ? readLocalJson(persistKey, []) : [])
  );
  const persistReadIds = React.useCallback(() => {
    if (persistKey) writeLocalJson(persistKey, [...readIdsRef.current]);
  }, [persistKey]);
  const ingest = React.useCallback((events, { prepend = false } = {}) => {
    if (events.length === 0) return;
    setItems((prev) => {
      const byId = new Map(prev.map((n) => [n.event.id, n]));
      for (const event of events) {
        if (byId.has(event.id)) continue;
        byId.set(
          event.id,
          toNotification(event, { read: readIdsRef.current.has(event.id) })
        );
      }
      const merged = [...byId.values()];
      return prepend ? merged.sort(byRecency) : merged;
    });
  }, []);
  const loadingRef = React.useRef(false);
  const requestSeq = React.useRef(0);
  const load = React.useCallback(
    async (nextCursor, activeFilter, { supersede = false } = {}) => {
      if (loadingRef.current && !supersede) return;
      const seq = ++requestSeq.current;
      loadingRef.current = true;
      setStatus((s) => s === "ready" && !supersede ? "ready" : "loading");
      setError(null);
      try {
        const page = await source.fetchHistory({
          cursor: nextCursor,
          limit: pageSize,
          filter: activeFilter
        });
        if (seq !== requestSeq.current) return;
        ingest(page.items);
        setCursor(page.nextCursor ?? null);
        setHasMore(Boolean(page.nextCursor));
        setStatus("ready");
      } catch (err) {
        if (seq !== requestSeq.current) return;
        log.error("fetchHistory failed", { error: String(err) });
        setError(err);
        setStatus("error");
      } finally {
        if (seq === requestSeq.current) loadingRef.current = false;
      }
    },
    [source, pageSize, ingest]
  );
  const filterKey = React.useMemo(() => serverFilterKey(filter), [filter]);
  const firstLoadRef = React.useRef(true);
  React.useEffect(() => {
    if (!autoLoad) return;
    const run = () => {
      setItems([]);
      setCursor(null);
      setHasMore(false);
      void load(null, filter, { supersede: true });
    };
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      run();
      return;
    }
    const timer = setTimeout(run, NC_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [source, filterKey, autoLoad]);
  const tenancyFiltered = isTenancyFiltered(filter);
  React.useEffect(() => {
    if (!source.subscribe) return;
    const unsubscribe = source.subscribe((event) => {
      if (!tenancyFiltered) ingest([event], { prepend: true });
      const level = criticalityFromName(event.criticality_level);
      if (toastAtOrAbove != null && level <= toastAtOrAbove) {
        notify({
          type: toastTypeFor(level),
          message: event.message_summary || criticalityLabel(level),
          description: event.message || void 0,
          id: event.id
        });
      }
    });
    return unsubscribe;
  }, [source, toastAtOrAbove, tenancyFiltered]);
  const loadMore = React.useCallback(() => {
    if (hasMore) void load(cursor, filter);
  }, [hasMore, cursor, load, filter]);
  const refresh = React.useCallback(() => {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    void load(null, filter, { supersede: true });
  }, [load, filter]);
  const markRead = React.useCallback(
    (id) => {
      if (readIdsRef.current.has(id)) return;
      readIdsRef.current.add(id);
      persistReadIds();
      setItems((prev) => prev.map((n) => n.event.id === id ? { ...n, read: true } : n));
      void source.markRead?.([id]).catch((err) => log.warn("markRead failed", { error: String(err) }));
    },
    [source, persistReadIds]
  );
  const markAllRead = React.useCallback(() => {
    if (items.length === 0) return;
    const ids = items.filter((n) => !n.read).map((n) => n.event.id);
    for (const n of items) readIdsRef.current.add(n.event.id);
    persistReadIds();
    setItems((prev) => prev.map((n) => n.read ? n : { ...n, read: true }));
    const filtered = Boolean(
      filter.criticalities?.length || filter.resources?.length || filter.search && filter.search.trim() || filter.unreadOnly || tenancyFiltered
    );
    const request = filtered && ids.length > 0 ? source.markRead?.(ids) : source.markAllRead?.();
    void request?.catch((err) => log.warn("markAllRead failed", { error: String(err) }));
  }, [items, source, persistReadIds, filter, tenancyFiltered]);
  const archive = React.useCallback(
    (id) => {
      setItems((prev) => prev.map((n) => n.event.id === id ? { ...n, archived: true } : n));
      void source.archive?.([id]).catch((err) => log.warn("archive failed", { error: String(err) }));
    },
    [source]
  );
  const notifications = React.useMemo(
    () => items.filter((n) => matchesFilter(n, filter)).sort(byRecency),
    [items, filter]
  );
  const unreadCount = React.useMemo(
    () => items.reduce((acc, n) => !n.read && !n.archived ? acc + 1 : acc, 0),
    [items]
  );
  return {
    notifications,
    all: items,
    unreadCount,
    status,
    error,
    filter,
    setFilter,
    hasMore,
    loadMore,
    refresh,
    markRead,
    markAllRead,
    archive
  };
}
function NotificationItem({
  notification,
  onSelect,
  onMarkRead,
  onArchive,
  present,
  className
}) {
  const { event, read, receivedAt } = notification;
  const presentation = React.useMemo(() => describeEvent(event, present), [event, present]);
  const handleActivate = () => {
    if (!read) onMarkRead?.(event.id);
    onSelect?.(notification);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "group relative flex items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent/50",
        !read && "bg-accent/30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: handleActivate,
            className: "flex min-w-0 flex-1 items-start gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm",
            children: [
              /* @__PURE__ */ jsx("span", { className: "mt-1.5", children: /* @__PURE__ */ jsx(CriticalityBadge, { level: event.criticality_level, dot: true }) }),
              /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
                /* @__PURE__ */ jsx("span", { className: cn("block truncate text-sm", !read ? "font-semibold" : "font-medium"), children: presentation.title }),
                presentation.description && /* @__PURE__ */ jsx("span", { className: "mt-0.5 block truncate text-xs text-muted-foreground", children: presentation.description }),
                /* @__PURE__ */ jsxs("span", { className: "mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground", children: [
                  /* @__PURE__ */ jsx(
                    CriticalityBadge,
                    {
                      level: event.criticality_level,
                      className: "h-4 px-1.5 py-0 text-[10px] font-medium leading-none"
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { className: "rounded bg-muted px-1.5 py-0.5", children: resourceLabel(event.resource) }),
                  /* @__PURE__ */ jsx(
                    "time",
                    {
                      dateTime: new Date(receivedAt).toISOString(),
                      title: `${criticalityLabel(event.criticality_level)} \xB7 ${formatTimestamp(receivedAt, { unit: "milliseconds" })}`,
                      children: formatRelativeTime(receivedAt, { unit: "milliseconds" })
                    }
                  )
                ] })
              ] })
            ]
          }
        ),
        onArchive && /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "ghost",
            size: "sm",
            "aria-label": "Archive notification",
            className: "shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
            onClick: () => onArchive(event.id),
            children: "Archive"
          }
        )
      ]
    }
  );
}
function NotificationList({
  controller,
  onSelect,
  hideArchive,
  emptyTitle = "You're all caught up",
  emptyDescription = "New notifications will show up here.",
  maxHeight,
  present,
  className
}) {
  const { notifications, status, error, hasMore, loadMore, refresh, markRead, archive } = controller;
  if (status === "error") {
    return /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refresh });
  }
  if (status === "loading" && notifications.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "space-y-2 p-3", "aria-busy": true, children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "mt-1 h-2 w-2 rounded-full" }),
      /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-1.5", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-3.5 w-3/4" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/3" })
      ] })
    ] }, i)) });
  }
  if (notifications.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: emptyTitle, description: emptyDescription, size: "sm" });
  }
  return /* @__PURE__ */ jsxs(ScrollArea, { className, style: maxHeight ? { maxHeight } : void 0, children: [
    /* @__PURE__ */ jsx("ul", { className: "divide-y divide-border", children: notifications.map((notification) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
      NotificationItem,
      {
        notification,
        onSelect,
        onMarkRead: markRead,
        onArchive: hideArchive ? void 0 : archive,
        present
      }
    ) }, notification.event.id)) }),
    hasMore && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx("div", { className: "p-2 text-center", children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: loadMore, disabled: status === "loading", children: status === "loading" ? "Loading\u2026" : "Load older" }) })
    ] })
  ] });
}
function BellIcon(props) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": true,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }),
        /* @__PURE__ */ jsx("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" })
      ]
    }
  );
}
function NotificationBell({
  onSelect,
  onViewAll,
  maxBadge = NC_DEFAULT_MAX_BADGE,
  present,
  className,
  ...options
}) {
  const controller = useNotifications(options);
  const { unreadCount, markAllRead } = controller;
  const badge = unreadCount > maxBadge ? `${maxBadge}+` : String(unreadCount);
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "ghost",
        size: "icon",
        className: cn("relative", className),
        "aria-label": unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications",
        children: [
          /* @__PURE__ */ jsx(BellIcon, { className: "h-5 w-5" }),
          unreadCount > 0 && /* @__PURE__ */ jsx(
            Badge,
            {
              variant: "brand",
              className: "absolute -right-1 -top-1 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] leading-none",
              children: badge
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "end", className: "w-96 p-0", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-3 py-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: "Notifications" }),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "link",
            size: "sm",
            className: "h-auto p-0 text-xs",
            disabled: unreadCount === 0,
            onClick: markAllRead,
            children: "Mark all read"
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx(
        NotificationList,
        {
          controller,
          onSelect,
          hideArchive: true,
          maxHeight: NC_POPOVER_MAX_HEIGHT,
          present
        }
      ),
      onViewAll && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Separator, {}),
        /* @__PURE__ */ jsx("div", { className: "p-2", children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", className: "w-full", onClick: onViewAll, children: "View all notifications" }) })
      ] })
    ] })
  ] });
}
var ANY = "";
function pick(values) {
  return (values?.length === 1 ? values[0] : void 0) ?? ANY;
}
function toList(value) {
  return value === ANY ? void 0 : [value];
}
function ScopeFilterBar({
  options,
  filter,
  onChange
}) {
  if (!options.canViewEveryone) return null;
  const { customers, organizations, tenants, users } = options;
  const field = (id, label, value, choices, anyLabel, onSelect) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsx(Label, { htmlFor: id, className: "text-xs text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(
      EnumSelect,
      {
        id,
        value,
        onChange: onSelect,
        className: "h-8 w-44",
        options: [{ value: ANY, label: anyLabel }, ...choices]
      }
    )
  ] });
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-end gap-3 rounded-md border border-dashed bg-muted/30 p-3", children: [
    customers && customers.length > 0 ? field(
      "nc-scope-customer",
      "Customer",
      pick(filter.customerIds),
      customers,
      "All customers",
      (next) => onChange((prev) => ({ ...prev, customerIds: toList(next) }))
    ) : null,
    organizations && organizations.length > 0 ? field(
      "nc-scope-org",
      "Organization",
      filter.orgId ?? ANY,
      organizations,
      "My organization",
      (next) => (
        // Switching org invalidates the tenant chosen inside the old one.
        onChange((prev) => ({
          ...prev,
          orgId: next === ANY ? void 0 : next,
          tenantId: void 0
        }))
      )
    ) : null,
    tenants && tenants.length > 0 ? field(
      "nc-scope-tenant",
      "Tenant",
      filter.tenantId ?? ANY,
      tenants,
      "My tenant",
      (next) => onChange((prev) => ({
        ...prev,
        tenantId: next === ANY ? void 0 : next
      }))
    ) : null,
    users && users.length > 0 ? field(
      "nc-scope-actor",
      "Acted by",
      pick(filter.actorUserIds),
      users,
      "Anyone",
      (next) => onChange((prev) => ({ ...prev, actorUserIds: toList(next) }))
    ) : null,
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: "nc-scope-everyone", className: "text-xs text-muted-foreground", children: "Recipients" }),
      /* @__PURE__ */ jsxs("div", { className: "flex h-8 items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Switch,
          {
            id: "nc-scope-everyone",
            checked: Boolean(filter.audienceAny),
            onCheckedChange: (checked) => onChange((prev) => ({ ...prev, audienceAny: checked || void 0 }))
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: filter.audienceAny ? "Everyone" : "Just me" })
      ] })
    ] })
  ] });
}
var ALL_RESOURCES = "";
function NotificationCenterPage({
  title = "Notifications",
  onSelect,
  present,
  scopeOptions,
  onFilterChange,
  className,
  ...options
}) {
  const controller = useNotifications(options);
  const { unreadCount, filter, setFilter, markAllRead } = controller;
  const activeResource = (filter.resources?.length === 1 ? filter.resources[0] : void 0) ?? ALL_RESOURCES;
  React.useEffect(() => {
    onFilterChange?.(filter);
  }, [filter]);
  const activeTab = React.useMemo(() => {
    const selected = filter.criticalities;
    return selected && selected.length === 1 ? String(selected[0]) : NC_ALL_TAB;
  }, [filter.criticalities]);
  const handleTabChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      criticalities: value === NC_ALL_TAB ? void 0 : [Number(value)]
    }));
  };
  return /* @__PURE__ */ jsxs(Card, { className, children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsxs(CardTitle, { className: "flex items-center gap-2", children: [
          title,
          unreadCount > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "brand", children: [
            unreadCount,
            " unread"
          ] })
        ] }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", disabled: unreadCount === 0, onClick: markAllRead, children: "Mark all read" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsx(Tabs, { value: activeTab, onValueChange: handleTabChange, children: /* @__PURE__ */ jsxs(TabsList, { children: [
          /* @__PURE__ */ jsx(TabsTrigger, { value: NC_ALL_TAB, children: "All" }),
          EVENT_CRITICALITY_ORDER.map((level) => /* @__PURE__ */ jsx(TabsTrigger, { value: String(level), children: criticalityLabel(level) }, level))
        ] }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-4", children: [
          /* @__PURE__ */ jsx(
            EnumSelect,
            {
              id: "nc-resource",
              value: activeResource,
              onChange: (next) => setFilter((prev) => ({
                ...prev,
                resources: next === ALL_RESOURCES ? void 0 : [next]
              })),
              className: "h-8 w-44",
              options: [
                { value: ALL_RESOURCES, label: "All activity" },
                ...EVENT_RESOURCE_ORDER.map((resource) => ({
                  value: resource,
                  label: resourceLabel(resource)
                }))
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(
              Switch,
              {
                id: "nc-unread-only",
                checked: Boolean(filter.unreadOnly),
                onCheckedChange: (checked) => setFilter((prev) => ({ ...prev, unreadOnly: checked }))
              }
            ),
            /* @__PURE__ */ jsx(Label, { htmlFor: "nc-unread-only", className: "text-sm text-muted-foreground", children: "Unread only" })
          ] }),
          /* @__PURE__ */ jsx(
            Input,
            {
              type: "search",
              placeholder: "Search notifications\u2026",
              className: "h-8 w-56",
              value: filter.search ?? "",
              onChange: (e) => setFilter((prev) => ({ ...prev, search: e.target.value }))
            }
          )
        ] })
      ] }),
      scopeOptions ? /* @__PURE__ */ jsx(
        ScopeFilterBar,
        {
          options: scopeOptions,
          filter,
          onChange: setFilter
        }
      ) : null
    ] }),
    /* @__PURE__ */ jsx(CardContent, { className: "p-0", children: /* @__PURE__ */ jsx(NotificationList, { controller, onSelect, present }) })
  ] });
}

export { NC_ALL_TAB, NC_DEFAULT_MAX_BADGE, NC_DEFAULT_PAGE_SIZE, NC_DEFAULT_TOAST_THRESHOLD, NC_FILTER_DEBOUNCE_MS, NC_PACKAGE, NC_POPOVER_MAX_HEIGHT, NC_READ_STATE_KEY, NC_STREAM, NotificationBell, NotificationCenterPage, NotificationItem, NotificationList, ScopeFilterBar, byRecency, createApiNotificationSource, createInMemoryNotificationSource, eventTimestampMs, isTenancyFiltered, matchesFilter, mergeNotificationSources, notificationChannelsFor, serverFilterKey, toNotification, useNotifications };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map